import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Statsig, StatsigContext, StatsigProvider } from "statsig-react";

import config from "#config";
import { getActiveStore, getUser } from "src/redux/selectors";

import { createStatsigDiagnostics } from "./diagnostics";

type StatsigReduxProviderProps = {
    children: React.ReactElement;
};

export const StatsigReduxProvider = ({
    children,
}: StatsigReduxProviderProps) => {
    const user = useSelector(getUser);
    const store = useSelector(getActiveStore);
    const requestSucceeded = useRef(true);
    const activeIdentity = useRef({ userID: user?._id, storeID: store?._id });
    activeIdentity.current = { userID: user?._id, storeID: store?._id };
    const diagnostics = useMemo(
        () => createStatsigDiagnostics(() => activeIdentity.current),
        [],
    );
    const onCompletion = (
        duration: number,
        success: boolean,
        message: string | null,
    ) => {
        requestSucceeded.current = success;
        diagnostics(success, duration, message);
    };

    // Statsig requires a real client SDK key. In local development, its hooks
    // safely fall back to disabled gates when rendered without the provider.
    if (config.env === "development" && !config.STATSIG_KEY) {
        return children;
    }

    return (
        <StatsigProvider
            sdkKey={config.STATSIG_KEY || ""}
            mountKey={user?._id}
            user={{
                userID: user?._id,
                email: user?.email,
                customIDs: {
                    storeID: store?._id || "",
                },
            }}
            waitForInitialization
            options={{
                environment: { tier: config.STATSIG_ENVIRONMENT },
                initCompletionCallback: onCompletion,
                updateUserCompletionCallback: onCompletion,
            }}
        >
            <RetryFailedEvaluations
                requestSucceeded={requestSucceeded}
                identity={JSON.stringify([user?._id, user?.email, store?._id])}
            >
                {children}
            </RetryFailedEvaluations>
        </StatsigProvider>
    );
};

function RetryFailedEvaluations({
    children,
    requestSucceeded,
    identity,
}: StatsigReduxProviderProps & {
    requestSucceeded: React.MutableRefObject<boolean>;
    identity: string;
}) {
    const context = useContext(StatsigContext);
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        if (!context.initialized || requestSucceeded.current) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        void (async () => {
            for (const delay of [1_000, 2_000, 4_000]) {
                await new Promise<void>((resolve) => {
                    timer = setTimeout(resolve, delay);
                });
                if (cancelled) return;
                const success = await Statsig.updateUser(
                    Statsig.getCurrentUser(),
                ).catch(() => false);
                if (cancelled) return;
                // Direct updateUser calls don't notify React gate consumers.
                setRevision((value) => value + 1);
                if (success) return;
            }
        })();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [context.initialized, context.userVersion, identity, requestSucceeded]);

    return (
        <StatsigContext.Provider
            value={{ ...context, userVersion: context.userVersion + revision }}
        >
            {children}
        </StatsigContext.Provider>
    );
}
